import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  toggleBold, toggleItalic, toggleStrikethrough, toggleInlineCode,
  clearFormatting,
  setHeading, setParagraph, toggleBlockquote, toggleCodeBlock,
  toggleBulletList, toggleOrderedList, toggleCheckList,
  indent, outdent,
  doUndo, doRedo,
  getActiveFormats,
  insertLink, insertImage, insertTable,
  insertHorizontalRule, insertDate, insertTOC, insertCallout, insertText,
  toggleUnderline, toggleSubscript, toggleSuperscript,
  insertTextColor, insertStatusLabel, insertCaption,
} from './editorCommands';
import {
  isInTable, getCellIndex,
  addRow, deleteRow, addColumn, deleteColumn,
  copyRow, copyColumn, addNumberColumn, getDeleteTableChange,
  cutColumn, pasteRowAbove, pasteColumnLeft,
} from './tableUtils';

// ═══════════════════════════════════════
// Detect macOS
// ═══════════════════════════════════════
const isMac = typeof navigator !== 'undefined' && /Mac/.test(navigator.platform);
const modKey = isMac ? '⌘' : 'Ctrl';
const shiftKey = isMac ? '⇧' : 'Shift';

// ═══════════════════════════════════════
// Custom Tooltip
// ═══════════════════════════════════════
const isTouchDevice = typeof window !== 'undefined' &&
  ('ontouchstart' in window || navigator.maxTouchPoints > 0);

const Tooltip = ({ label, shortcut, children }) => {
  const [show, setShow] = useState(false);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const timerRef = useRef(null);
  const btnRef = useRef(null);

  const handleEnter = () => {
    if (isTouchDevice) return; // No tooltips on touch devices
    timerRef.current = setTimeout(() => {
      if (btnRef.current) {
        const rect = btnRef.current.getBoundingClientRect();
        let x = rect.left + rect.width / 2;
        const y = rect.bottom + 6;
        // Clamp to viewport edges (8px padding)
        const tooltipWidth = 120; // estimated max width
        if (x - tooltipWidth / 2 < 8) x = tooltipWidth / 2 + 8;
        if (x + tooltipWidth / 2 > window.innerWidth - 8) x = window.innerWidth - tooltipWidth / 2 - 8;
        setPos({ x, y });
        setShow(true);
      }
    }, 400);
  };

  const handleLeave = () => {
    clearTimeout(timerRef.current);
    setShow(false);
  };

  return (
    <span ref={btnRef} onMouseEnter={handleEnter} onMouseLeave={handleLeave}
      style={{ display: 'inline-flex' }}>
      {children}
      {show && (
        <div className="et-tooltip" style={{ left: pos.x, top: pos.y }}>
          <span className="et-tooltip-label">{label}</span>
          {shortcut && <span className="et-tooltip-shortcut">{shortcut}</span>}
        </div>
      )}
    </span>
  );
};

// Tooltip-wrapped button
const TBtn = ({ label, shortcut, className, ...props }) => (
  <Tooltip label={label} shortcut={shortcut}>
    <button className={className || 'et-btn'} {...props} />
  </Tooltip>
);

// ═══════════════════════════════════════
// SVG Icons (16x16, stroke 1.8)
// ═══════════════════════════════════════

const Icon = ({ children, size = 16, ...props }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"
    strokeLinejoin="round" {...props}>
    {children}
  </svg>
);

const BoldIcon = () => <Icon><path d="M6 4h8a4 4 0 0 1 0 8H6z"/><path d="M6 12h9a4 4 0 0 1 0 8H6z"/></Icon>;
const ItalicIcon = () => <Icon><line x1="19" y1="4" x2="10" y2="4"/><line x1="14" y1="20" x2="5" y2="20"/><line x1="15" y1="4" x2="9" y2="20"/></Icon>;
const StrikethroughIcon = () => <Icon><path d="M16 4H9a3 3 0 0 0 0 6"/><line x1="4" y1="12" x2="20" y2="12"/><path d="M15 12a3.5 3.5 0 0 1 0 7H8"/></Icon>;
const CodeIcon = () => <Icon><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></Icon>;

const BulletListIcon = () => <Icon><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><circle cx="4" cy="6" r="1" fill="currentColor" stroke="none"/><circle cx="4" cy="12" r="1" fill="currentColor" stroke="none"/><circle cx="4" cy="18" r="1" fill="currentColor" stroke="none"/></Icon>;
const OrderedListIcon = () => <Icon><line x1="10" y1="6" x2="21" y2="6"/><line x1="10" y1="12" x2="21" y2="12"/><line x1="10" y1="18" x2="21" y2="18"/><text x="2" y="8" fontSize="8" fill="currentColor" stroke="none" fontFamily="sans-serif">1</text><text x="2" y="14" fontSize="8" fill="currentColor" stroke="none" fontFamily="sans-serif">2</text><text x="2" y="20" fontSize="8" fill="currentColor" stroke="none" fontFamily="sans-serif">3</text></Icon>;
const CheckListIcon = () => <Icon><rect x="3" y="5" width="6" height="6" rx="1"/><polyline points="5 8 6.5 9.5 9 6" strokeWidth="1.5"/><line x1="12" y1="8" x2="21" y2="8"/><rect x="3" y="14" width="6" height="6" rx="1"/><line x1="12" y1="17" x2="21" y2="17"/></Icon>;

const IndentIcon = () => <Icon><polyline points="13 8 17 12 13 16"/><line x1="3" y1="4" x2="21" y2="4"/><line x1="3" y1="20" x2="21" y2="20"/><line x1="3" y1="12" x2="11" y2="12"/></Icon>;
const OutdentIcon = () => <Icon><polyline points="11 8 7 12 11 16"/><line x1="3" y1="4" x2="21" y2="4"/><line x1="3" y1="20" x2="21" y2="20"/><line x1="13" y1="12" x2="21" y2="12"/></Icon>;

const UndoIcon = () => <Icon><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/></Icon>;
const RedoIcon = () => <Icon><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.13-9.36L23 10"/></Icon>;

const LinkIcon = () => <Icon><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></Icon>;
const ImageIcon = () => <Icon><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></Icon>;
const TableIcon = () => <Icon><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="3" y1="15" x2="21" y2="15"/><line x1="9" y1="3" x2="9" y2="21"/><line x1="15" y1="3" x2="15" y2="21"/></Icon>;
const EmojiIcon = () => <Icon><circle cx="12" cy="12" r="10"/><path d="M8 14s1.5 2 4 2 4-2 4-2"/><line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/></Icon>;
const PlusIcon = () => <Icon><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></Icon>;

const UnderlineIcon = () => <Icon><path d="M6 3v7a6 6 0 0 0 12 0V3"/><line x1="4" y1="21" x2="20" y2="21"/></Icon>;
const SubscriptIcon = () => <Icon><text x="3" y="16" fontSize="14" fill="currentColor" stroke="none" fontFamily="serif">x</text><text x="14" y="20" fontSize="9" fill="currentColor" stroke="none" fontFamily="serif">2</text></Icon>;
const SuperscriptIcon = () => <Icon><text x="3" y="18" fontSize="14" fill="currentColor" stroke="none" fontFamily="serif">x</text><text x="14" y="10" fontSize="9" fill="currentColor" stroke="none" fontFamily="serif">2</text></Icon>;
const TextColorIcon = () => <Icon><path d="M9.9 4.24A.5.5 0 0 1 10.5 4h3a.5.5 0 0 1 .474.658l-4.5 13A.5.5 0 0 1 9 18H6a.5.5 0 0 1-.474-.658z" fill="none"/><line x1="5" y1="18" x2="10" y2="18" strokeWidth="2"/><line x1="14" y1="18" x2="19" y2="18" strokeWidth="2"/><text x="7" y="16" fontSize="15" fill="currentColor" stroke="none" fontWeight="bold" fontFamily="serif">A</text><rect x="3" y="20" width="18" height="3" rx="0.5" fill="currentColor" stroke="none"/></Icon>;
const SpecialCharIcon = () => <Icon><text x="4" y="18" fontSize="16" fill="currentColor" stroke="none" fontFamily="serif">Ω</text></Icon>;
const ClipboardPasteIcon = () => <Icon size={14}><path d="M15 2H9a1 1 0 0 0-1 1v1H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2h-3V3a1 1 0 0 0-1-1z"/><line x1="9" y1="12" x2="15" y2="12"/><line x1="12" y1="15" x2="12" y2="9"/></Icon>;
const CaptionIcon = () => <Icon size={14}><rect x="2" y="4" width="20" height="12" rx="2"/><line x1="6" y1="20" x2="18" y2="20" strokeWidth="1.5"/></Icon>;

const ChevronDownIcon = () => <Icon size={12}><polyline points="6 9 12 15 18 9"/></Icon>;
const MoreIcon = () => <Icon><circle cx="12" cy="12" r="1"/><circle cx="19" cy="12" r="1"/><circle cx="5" cy="12" r="1"/></Icon>;

// Table context icons (smaller)
const RowAboveIcon = () => <Icon size={14}><rect x="3" y="3" width="18" height="18" rx="1"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="12" y1="3" x2="12" y2="12"/><polyline points="9 7 12 4 15 7" strokeWidth="2"/></Icon>;
const RowBelowIcon = () => <Icon size={14}><rect x="3" y="3" width="18" height="18" rx="1"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="12" y1="12" x2="12" y2="21"/><polyline points="9 17 12 20 15 17" strokeWidth="2"/></Icon>;
const ColLeftIcon = () => <Icon size={14}><rect x="3" y="3" width="18" height="18" rx="1"/><line x1="12" y1="3" x2="12" y2="21"/><line x1="3" y1="12" x2="12" y2="12"/><polyline points="7 9 4 12 7 15" strokeWidth="2"/></Icon>;
const ColRightIcon = () => <Icon size={14}><rect x="3" y="3" width="18" height="18" rx="1"/><line x1="12" y1="3" x2="12" y2="21"/><line x1="12" y1="12" x2="21" y2="12"/><polyline points="17 9 20 12 17 15" strokeWidth="2"/></Icon>;
const DeleteRowIcon = () => <Icon size={14}><line x1="4" y1="12" x2="20" y2="12"/><line x1="8" y1="8" x2="16" y2="16"/><line x1="16" y1="8" x2="8" y2="16"/></Icon>;
const DeleteColIcon = () => <Icon size={14}><line x1="12" y1="4" x2="12" y2="20"/><line x1="8" y1="8" x2="16" y2="16"/><line x1="16" y1="8" x2="8" y2="16"/></Icon>;
const HashIcon = () => <Icon size={14}><line x1="4" y1="9" x2="20" y2="9"/><line x1="4" y1="15" x2="20" y2="15"/><line x1="10" y1="3" x2="8" y2="21"/><line x1="16" y1="3" x2="14" y2="21"/></Icon>;
const TrashIcon = () => <Icon size={14}><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></Icon>;
const CopyIcon = () => <Icon size={14}><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></Icon>;
const ScissorsIcon = () => <Icon size={14}><circle cx="6" cy="6" r="3"/><circle cx="6" cy="18" r="3"/><line x1="20" y1="4" x2="8.12" y2="15.88"/><line x1="14.47" y1="14.48" x2="20" y2="20"/><line x1="8.12" y1="8.12" x2="12" y2="12"/></Icon>;

// Context icons
const ExternalLinkIcon = () => <Icon size={14}><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></Icon>;
const UnlinkIcon = () => <Icon size={14}><path d="M18.84 12.25l1.72-1.71a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M5.16 11.75l-1.72 1.71a5 5 0 0 0 7.07 7.07l1.72-1.71"/><line x1="8" y1="2" x2="8" y2="5"/><line x1="2" y1="8" x2="5" y2="8"/><line x1="16" y1="19" x2="16" y2="22"/><line x1="19" y1="16" x2="22" y2="16"/></Icon>;
const EditIcon = () => <Icon size={14}><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></Icon>;

// ═══════════════════════════════════════
// Data
// ═══════════════════════════════════════

const HEADING_OPTIONS = [
  { label: '본문', value: 0, preview: '가나다라마바사 12345', size: '14px', weight: 400 },
  { label: '제목 1', value: 1, preview: '가나다라마바사', size: '28px', weight: 700 },
  { label: '제목 2', value: 2, preview: '가나다라마바사', size: '22px', weight: 700 },
  { label: '제목 3', value: 3, preview: '가나다라마바사', size: '18px', weight: 600 },
  { label: '제목 4', value: 4, preview: '가나다라마바사', size: '16px', weight: 600 },
  { label: '제목 5', value: 5, preview: '가나다라마바사', size: '14px', weight: 600 },
  { label: '제목 6', value: 6, preview: '가나다라마바사', size: '13px', weight: 600 },
  { label: '인용문', value: 'quote', preview: '인용문 블록', size: '14px', weight: 400, italic: true },
  { label: '코드 블록', value: 'code', preview: 'code block', size: '13px', weight: 400, mono: true },
];

const EMOJI_CATEGORIES = [
  { name: '자주', emojis: ['👍', '❤️', '😀', '😂', '🎉', '🔥', '✅', '❌', '⭐', '💡', '📌', '🚀', '⚠️', '📝', '🎯', '💪'] },
  { name: '표정', emojis: ['😀', '😃', '😄', '😁', '😆', '😅', '🤣', '😂', '🙂', '😊', '😇', '🥰', '😍', '🤔', '😐', '😤', '😭', '😱', '🤗', '🥳'] },
  { name: '손', emojis: ['👍', '👎', '👏', '🙌', '🤝', '✌️', '🤞', '👋', '✋', '👌', '🤙', '💪', '🙏', '☝️', '👆', '🖐️'] },
  { name: '기호', emojis: ['✅', '❌', '⭐', '💡', '🔥', '❤️', '💔', '⚡', '🎯', '🏆', '🎉', '📌', '📎', '🔗', '⚠️', '🚫', '❓', '❗', '💬', '📝'] },
  { name: '화살표', emojis: ['➡️', '⬅️', '⬆️', '⬇️', '↗️', '↘️', '↙️', '↖️', '↩️', '↪️', '🔄', '🔃', '▶️', '◀️', '🔼', '🔽'] },
];

const SPECIAL_CHARS = [
  { name: '수학', chars: ['±', '×', '÷', '≠', '≈', '≤', '≥', '∞', '√', '∑', '∏', 'π', 'μ', 'Δ', '∫', '∂'] },
  { name: '화폐', chars: ['$', '€', '£', '¥', '₩', '₿', '¢', '₹', '₽', '₸', '₫', '₪'] },
  { name: '기호', chars: ['©', '®', '™', '§', '¶', '†', '‡', '°', '‰', '•', '…', '―', '–', '—', '¦', '※'] },
  { name: '화살표', chars: ['←', '→', '↑', '↓', '↔', '↕', '⇐', '⇒', '⇑', '⇓', '⇔', '↗', '↘', '↙', '↖', '↻'] },
  { name: '그리스', chars: ['α', 'β', 'γ', 'δ', 'ε', 'θ', 'λ', 'σ', 'τ', 'φ', 'ω', 'Ω', 'Σ', 'Π', 'Φ', 'Ψ'] },
];

const TEXT_COLORS = [
  { name: '빨강', color: '#dc2626' },
  { name: '주황', color: '#ea580c' },
  { name: '노랑', color: '#ca8a04' },
  { name: '초록', color: '#16a34a' },
  { name: '파랑', color: '#2563eb' },
  { name: '보라', color: '#7c3aed' },
  { name: '분홍', color: '#db2777' },
  { name: '회색', color: '#6b7280' },
];

const STATUS_LABELS = [
  { label: '진행중', value: '진행중' },
  { label: '완료', value: '완료' },
  { label: '보류', value: '보류' },
  { label: '긴급', value: '긴급' },
  { label: 'TODO', value: 'TODO' },
  { label: 'DONE', value: 'DONE' },
  { label: 'WIP', value: 'WIP' },
  { label: 'BLOCKED', value: 'BLOCKED' },
];

const CODE_LANGUAGES = [
  'javascript', 'typescript', 'python', 'java', 'go', 'rust', 'c', 'cpp',
  'html', 'css', 'json', 'yaml', 'sql', 'bash', 'markdown', 'plaintext',
];

// ═══════════════════════════════════════
// Sub-components
// ═══════════════════════════════════════

function useClickOutside(ref, isOpen, onClose) {
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        onClose();
      }
    };
    // Defer listener attachment to next frame so the current mousedown event
    // (which triggered the popup to open) doesn't immediately close it.
    const raf = requestAnimationFrame(() => {
      document.addEventListener('mousedown', handler);
    });
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('mousedown', handler);
    };
  }, [ref, isOpen, onClose]);
}

// Portal-based popup positioning — escapes overflow:auto containers
const PortalPopup = ({ anchorRef, isOpen, children, className }) => {
  const [pos, setPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (!isOpen || !anchorRef?.current) return;
    const update = () => {
      const rect = anchorRef.current.getBoundingClientRect();
      let left = rect.left;
      // Clamp to viewport right edge
      const popupWidth = 300;
      if (left + popupWidth > window.innerWidth - 8) {
        left = window.innerWidth - popupWidth - 8;
      }
      if (left < 8) left = 8;
      setPos({ top: rect.bottom + 4, left });
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [isOpen, anchorRef]);

  if (!isOpen) return null;
  return createPortal(
    <div className={className} style={{ position: 'fixed', top: pos.top, left: pos.left, zIndex: 10000 }}>
      {children}
    </div>,
    document.body
  );
};

const Dropdown = ({ trigger, children, isOpen, onToggle, onClose }) => {
  const ref = useRef(null);
  const btnRef = useRef(null);
  useClickOutside(ref, isOpen, onClose);
  return (
    <div className="et-dropdown">
      <button ref={btnRef} className="et-btn" onMouseDown={e => { e.preventDefault(); onToggle(); }}>{trigger}</button>
      {isOpen && createPortal(
        <div ref={ref} className="et-dropdown-menu" style={{
          position: 'fixed',
          top: btnRef.current ? btnRef.current.getBoundingClientRect().bottom + 4 : 0,
          left: btnRef.current ? Math.max(8, btnRef.current.getBoundingClientRect().left) : 0,
          zIndex: 10000,
        }}>{children}</div>,
        document.body
      )}
    </div>
  );
};

// ─── Link Popup ───
const LinkPopup = ({ isOpen, onClose, onInsert, initialText, initialUrl, className }) => {
  const [tab, setTab] = useState('url'); // 'url' | 'doc'
  const [text, setText] = useState('');
  const [url, setUrl] = useState('');
  const [search, setSearch] = useState('');
  const [docs, setDocs] = useState([]);
  const [searching, setSearching] = useState(false);
  const ref = useRef(null);
  const inputRef = useRef(null);
  const searchTimerRef = useRef(null);
  useClickOutside(ref, isOpen, onClose);

  useEffect(() => {
    if (isOpen) {
      setText(initialText || '');
      setUrl(initialUrl || '');
      setSearch('');
      setDocs([]);
      setTab(initialUrl ? 'url' : 'url');
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen, initialText, initialUrl]);

  // Search documents as user types
  useEffect(() => {
    if (!search.trim() || tab !== 'doc') { setDocs([]); return; }
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const apiBase = window.location.hostname === 'localhost'
          ? 'http://localhost:4009'
          : 'https://markdown.ilouli.com';
        const res = await fetch(`${apiBase}/api/documents?search=${encodeURIComponent(search.trim())}&limit=10`, {
          credentials: 'include',
        });
        const data = await res.json();
        if (data.success) setDocs(data.data || []);
      } catch {}
      setSearching(false);
    }, 300);
    return () => clearTimeout(searchTimerRef.current);
  }, [search, tab]);

  // Load recent docs when switching to doc tab
  useEffect(() => {
    if (tab === 'doc' && docs.length === 0 && !search.trim()) {
      (async () => {
        try {
          const apiBase = window.location.hostname === 'localhost'
            ? 'http://localhost:4009'
            : 'https://markdown.ilouli.com';
          const res = await fetch(`${apiBase}/api/documents?limit=10`, { credentials: 'include' });
          const data = await res.json();
          if (data.success) setDocs(data.data || []);
        } catch {}
      })();
    }
  }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectDoc = (doc) => {
    const docUrl = `/editor/${doc.id}`;
    onInsert(text || doc.title, docUrl);
    onClose();
  };

  if (!isOpen) return null;
  return (
    <div ref={ref} className={className || ''}>
      <div className="et-popup-title">링크 삽입</div>
      <div className="et-popup-tabs">
        <button className={`et-popup-tab ${tab === 'url' ? 'active' : ''}`}
          onMouseDown={e => { e.preventDefault(); setTab('url'); }}>URL 입력</button>
        <button className={`et-popup-tab ${tab === 'doc' ? 'active' : ''}`}
          onMouseDown={e => { e.preventDefault(); setTab('doc'); }}>문서 검색</button>
      </div>
      {tab === 'url' ? (
        <form onSubmit={e => { e.preventDefault(); if (url) { onInsert(text, url); onClose(); } }} className="et-popup-form">
          <input ref={inputRef} type="text" placeholder="텍스트" value={text} onChange={e => setText(e.target.value)} className="et-popup-input" />
          <input type="url" placeholder="https://..." value={url} onChange={e => setUrl(e.target.value)} className="et-popup-input" />
          <div className="et-popup-actions">
            <button type="button" onClick={onClose} className="et-popup-btn cancel">취소</button>
            <button type="submit" disabled={!url} className="et-popup-btn primary">삽입</button>
          </div>
        </form>
      ) : (
        <div className="et-popup-form">
          <input type="text" placeholder="문서 제목 검색..." value={search}
            onChange={e => setSearch(e.target.value)} className="et-popup-input" autoFocus />
          <input type="text" placeholder="표시 텍스트 (선택)" value={text}
            onChange={e => setText(e.target.value)} className="et-popup-input" />
          <div className="et-doc-list">
            {searching && <div className="et-doc-item et-doc-loading">검색 중...</div>}
            {!searching && docs.length === 0 && search.trim() && (
              <div className="et-doc-item et-doc-empty">결과 없음</div>
            )}
            {docs.map(doc => (
              <button key={doc.id} className="et-doc-item"
                onMouseDown={e => { e.preventDefault(); selectDoc(doc); }}>
                <span className="et-doc-title">{doc.title}</span>
                <span className="et-doc-date">{doc.updated_at?.split('T')[0]}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Image Popup ───
const ImagePopup = ({ isOpen, onClose, onInsert }) => {
  const [tab, setTab] = useState('upload'); // 'upload' | 'url'
  const [alt, setAlt] = useState('');
  const [url, setUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const ref = useRef(null);
  const fileInputRef = useRef(null);
  useClickOutside(ref, isOpen, onClose);
  useEffect(() => { if (isOpen) { setAlt(''); setUrl(''); setTab('upload'); setUploading(false); setDragOver(false); } }, [isOpen]);

  const uploadFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const apiBase = window.location.hostname === 'localhost'
        ? `http://localhost:4009`
        : `https://markdown.ilouli.com`;
      const res = await fetch(`${apiBase}/api/uploads/image`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });
      const data = await res.json();
      if (data.success) {
        const imgUrl = data.data.url.startsWith('http') ? data.data.url : `${apiBase}${data.data.url}`;
        onInsert(alt || file.name.replace(/\.[^.]+$/, ''), imgUrl);
        onClose();
      } else {
        alert(data.error || '업로드 실패');
      }
    } catch (err) {
      alert('업로드 실패: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file && file.type.startsWith('image/')) uploadFile(file);
  };

  if (!isOpen) return null;
  return (
    <div ref={ref}>
      <div className="et-popup-title">이미지 삽입</div>
      <div className="et-popup-tabs">
        <button className={`et-popup-tab ${tab === 'upload' ? 'active' : ''}`}
          onMouseDown={e => { e.preventDefault(); setTab('upload'); }}>파일 업로드</button>
        <button className={`et-popup-tab ${tab === 'url' ? 'active' : ''}`}
          onMouseDown={e => { e.preventDefault(); setTab('url'); }}>URL 입력</button>
      </div>
      {tab === 'upload' ? (
        <div className="et-popup-form">
          <div className={`et-drop-zone ${dragOver ? 'drag-over' : ''}`}
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}>
            <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }}
              onChange={e => uploadFile(e.target.files?.[0])} />
            {uploading
              ? <span className="et-drop-text">업로드 중...</span>
              : <span className="et-drop-text">클릭하거나 이미지를 드래그하세요</span>}
          </div>
          <input type="text" placeholder="대체 텍스트 (선택)" value={alt}
            onChange={e => setAlt(e.target.value)} className="et-popup-input" />
        </div>
      ) : (
        <form onSubmit={e => { e.preventDefault(); if (url) { onInsert(alt, url); onClose(); } }} className="et-popup-form">
          <input type="text" placeholder="대체 텍스트 (alt)" value={alt} onChange={e => setAlt(e.target.value)} className="et-popup-input" />
          <input type="url" placeholder="https://..." value={url} onChange={e => setUrl(e.target.value)} className="et-popup-input" autoFocus />
          <div className="et-popup-actions">
            <button type="button" onClick={onClose} className="et-popup-btn cancel">취소</button>
            <button type="submit" disabled={!url} className="et-popup-btn primary">삽입</button>
          </div>
        </form>
      )}
    </div>
  );
};

// ─── Table Grid Picker ───
const TableGridPicker = ({ isOpen, onClose, onInsert }) => {
  const [hoverRow, setHoverRow] = useState(0);
  const [hoverCol, setHoverCol] = useState(0);
  const ref = useRef(null);
  useClickOutside(ref, isOpen, onClose);
  if (!isOpen) return null;
  return (
    <div ref={ref}>
      <div className="et-popup-title">표 삽입</div>
      <div className="et-table-grid">
        {Array.from({ length: 8 }, (_, r) => (
          <div key={r} className="et-table-grid-row">
            {Array.from({ length: 8 }, (_, c) => (
              <div key={c}
                className={`et-table-grid-cell ${r <= hoverRow && c <= hoverCol ? 'active' : ''}`}
                onMouseEnter={() => { setHoverRow(r); setHoverCol(c); }}
                onMouseDown={e => { e.preventDefault(); onInsert(r + 1, c + 1); onClose(); }}
              />
            ))}
          </div>
        ))}
      </div>
      <div className="et-table-grid-label">{hoverCol + 1} x {hoverRow + 1}</div>
    </div>
  );
};

// ─── Emoji Picker ───
const EmojiPicker = ({ isOpen, onClose, onInsert }) => {
  const [cat, setCat] = useState(0);
  const ref = useRef(null);
  useClickOutside(ref, isOpen, onClose);
  if (!isOpen) return null;
  return (
    <div ref={ref}>
      <div className="et-emoji-tabs">
        {EMOJI_CATEGORIES.map((c, i) => (
          <button key={c.name} className={`et-emoji-tab ${i === cat ? 'active' : ''}`}
            onMouseDown={e => { e.preventDefault(); setCat(i); }}>{c.name}</button>
        ))}
      </div>
      <div className="et-emoji-grid">
        {EMOJI_CATEGORIES[cat].emojis.map((emoji, i) => (
          <button key={`${emoji}-${i}`} className="et-emoji-cell"
            onMouseDown={e => { e.preventDefault(); onInsert(emoji); onClose(); }}>{emoji}</button>
        ))}
      </div>
    </div>
  );
};

// ─── Special Character Picker ───
const SpecialCharPicker = ({ isOpen, onClose, onInsert }) => {
  const [cat, setCat] = useState(0);
  const ref = useRef(null);
  useClickOutside(ref, isOpen, onClose);
  if (!isOpen) return null;
  return (
    <div ref={ref}>
      <div className="et-popup-title">특수문자 삽입</div>
      <div className="et-emoji-tabs">
        {SPECIAL_CHARS.map((c, i) => (
          <button key={c.name} className={`et-emoji-tab ${i === cat ? 'active' : ''}`}
            onMouseDown={e => { e.preventDefault(); setCat(i); }}>{c.name}</button>
        ))}
      </div>
      <div className="et-emoji-grid">
        {SPECIAL_CHARS[cat].chars.map((ch, i) => (
          <button key={`${ch}-${i}`} className="et-emoji-cell"
            onMouseDown={e => { e.preventDefault(); onInsert(ch); onClose(); }}>{ch}</button>
        ))}
      </div>
    </div>
  );
};

// ─── Text Color Picker ───
const TextColorPicker = ({ isOpen, onClose, onApply }) => {
  const ref = useRef(null);
  useClickOutside(ref, isOpen, onClose);
  if (!isOpen) return null;
  return (
    <div ref={ref}>
      <div className="et-popup-title">텍스트 색상</div>
      <div className="et-color-grid">
        {TEXT_COLORS.map(c => (
          <button key={c.color} className="et-color-cell"
            title={c.name}
            onMouseDown={e => { e.preventDefault(); onApply(c.color); onClose(); }}>
            <span className="et-color-swatch" style={{ backgroundColor: c.color }} />
            <span className="et-color-name">{c.name}</span>
          </button>
        ))}
      </div>
      <button className="et-dropdown-item" style={{ marginTop: 4, fontSize: 12 }}
        onMouseDown={e => { e.preventDefault(); onApply('remove'); onClose(); }}>
        색상 제거
      </button>
    </div>
  );
};

// ─── Status Label Picker ───
const StatusLabelPicker = ({ isOpen, onClose, onInsert }) => {
  const ref = useRef(null);
  useClickOutside(ref, isOpen, onClose);
  if (!isOpen) return null;
  return (
    <div ref={ref}>
      <div className="et-popup-title">상태 라벨</div>
      <div className="et-status-grid">
        {STATUS_LABELS.map(s => (
          <button key={s.value} className="et-status-cell"
            onMouseDown={e => { e.preventDefault(); onInsert(s.value); onClose(); }}>
            {s.label}
          </button>
        ))}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════
// Context Detection (Phase 4)
// ═══════════════════════════════════════

function getContextInfo(state) {
  if (!state) return {};
  const pos = state.selection.main.head;
  const line = state.doc.lineAt(pos);
  const text = line.text;
  const offset = pos - line.from;

  const inTable = isInTable(state, pos);
  const tableCell = inTable ? getCellIndex(state, pos) : null;

  let linkMatch = null;
  const linkRegex = /\[([^\]]*)\]\(([^)]*)\)/g;
  let m;
  while ((m = linkRegex.exec(text)) !== null) {
    if (offset >= m.index && offset <= m.index + m[0].length) {
      linkMatch = { text: m[1], url: m[2], from: line.from + m.index, to: line.from + m.index + m[0].length };
      break;
    }
  }

  let imageMatch = null;
  const imgRegex = /!\[([^\]]*)\]\(([^)]*)\)/g;
  while ((m = imgRegex.exec(text)) !== null) {
    if (offset >= m.index && offset <= m.index + m[0].length) {
      imageMatch = { alt: m[1], url: m[2], from: line.from + m.index, to: line.from + m.index + m[0].length };
      break;
    }
  }

  let inCodeBlock = false;
  let codeBlockLang = '';
  let codeBlockFrom = 0;
  let codeBlockTo = 0;
  const doc = state.doc;
  let fenceCount = 0;
  let lastFenceLang = '';
  for (let i = 1; i <= doc.lines; i++) {
    const l = doc.line(i);
    if (l.text.trimStart().startsWith('```')) {
      if (fenceCount % 2 === 0) {
        lastFenceLang = l.text.trimStart().slice(3).trim();
        codeBlockFrom = l.from;
      } else {
        codeBlockTo = l.to;
      }
      fenceCount++;
    }
    if (i === line.number && fenceCount % 2 === 1) {
      inCodeBlock = true;
      codeBlockLang = lastFenceLang;
      break;
    }
  }
  if (inCodeBlock && codeBlockTo === 0) {
    for (let i = line.number + 1; i <= doc.lines; i++) {
      const l = doc.line(i);
      if (l.text.trimStart().startsWith('```')) { codeBlockTo = l.to; break; }
    }
    if (codeBlockTo === 0) codeBlockTo = doc.length;
  }

  const listMatch = text.match(/^(\s*)([-*+])\s(\[[ x]\]\s)?/);
  const orderedMatch = text.match(/^(\s*)(\d+)\.\s/);
  const inList = !!(listMatch || orderedMatch);
  const listType = listMatch ? (listMatch[3] ? 'check' : 'bullet') : orderedMatch ? 'ordered' : null;

  return {
    inTable, tableCell,
    link: linkMatch, image: imageMatch,
    inCodeBlock, codeBlockLang, codeBlockFrom, codeBlockTo,
    inList, listType,
  };
}

// ═══════════════════════════════════════
// Table Context Sub-Menu (Phase 3)
// ═══════════════════════════════════════

const TableContextMenu = ({ editorView }) => {
  const act = useCallback((fn, ...args) => {
    if (!editorView) return;
    const change = fn(editorView.state, ...args);
    if (change) { editorView.dispatch({ changes: change }); editorView.focus(); }
  }, [editorView]);

  const copy = useCallback(async (fn) => {
    if (!editorView) return;
    const text = fn(editorView.state);
    if (text) try { await navigator.clipboard.writeText(text); } catch {}
  }, [editorView]);

  return (
    <div className="et-context-bar">
      <div className="et-context-label">표</div>
      <div className="et-group">
        <TBtn label="위에 행 삽입" className="et-btn et-btn-sm" onMouseDown={e => { e.preventDefault(); act(addRow, 'above'); }}><RowAboveIcon /></TBtn>
        <TBtn label="아래 행 삽입" className="et-btn et-btn-sm" onMouseDown={e => { e.preventDefault(); act(addRow, 'below'); }}><RowBelowIcon /></TBtn>
        <TBtn label="행 삭제" className="et-btn et-btn-sm" onMouseDown={e => { e.preventDefault(); act(deleteRow); }}><DeleteRowIcon /></TBtn>
      </div>
      <div className="et-sep-sm" />
      <div className="et-group">
        <TBtn label="행 잘라내기" className="et-btn et-btn-sm" onMouseDown={e => { e.preventDefault(); copy(copyRow); act(deleteRow); }}><ScissorsIcon /></TBtn>
        <TBtn label="행 복사" className="et-btn et-btn-sm" onMouseDown={e => { e.preventDefault(); copy(copyRow); }}><CopyIcon /></TBtn>
      </div>
      <div className="et-sep-sm" />
      <div className="et-group">
        <TBtn label="왼쪽 열 삽입" className="et-btn et-btn-sm" onMouseDown={e => { e.preventDefault(); act(addColumn, 'left'); }}><ColLeftIcon /></TBtn>
        <TBtn label="오른쪽 열 삽입" className="et-btn et-btn-sm" onMouseDown={e => { e.preventDefault(); act(addColumn, 'right'); }}><ColRightIcon /></TBtn>
        <TBtn label="열 삭제" className="et-btn et-btn-sm" onMouseDown={e => { e.preventDefault(); act(deleteColumn); }}><DeleteColIcon /></TBtn>
      </div>
      <div className="et-sep-sm" />
      <div className="et-group">
        <TBtn label="열 잘라내기" className="et-btn et-btn-sm" onMouseDown={async e => { e.preventDefault();
          if (!editorView) return;
          const result = cutColumn(editorView.state);
          if (result) {
            try { await navigator.clipboard.writeText(result.clipText); } catch {}
            editorView.dispatch({ changes: result.change });
            editorView.focus();
          }
        }}><ScissorsIcon /></TBtn>
        <TBtn label="열 복사" className="et-btn et-btn-sm" onMouseDown={e => { e.preventDefault(); copy(copyColumn); }}><CopyIcon /></TBtn>
        <TBtn label="위에 행 붙여넣기" className="et-btn et-btn-sm" onMouseDown={async e => { e.preventDefault();
          if (!editorView) return;
          try {
            const clip = await navigator.clipboard.readText();
            const change = pasteRowAbove(editorView.state, clip);
            if (change) { editorView.dispatch({ changes: change }); editorView.focus(); }
          } catch {}
        }}><ClipboardPasteIcon /></TBtn>
        <TBtn label="왼쪽 열에 붙여넣기" className="et-btn et-btn-sm" onMouseDown={async e => { e.preventDefault();
          if (!editorView) return;
          try {
            const clip = await navigator.clipboard.readText();
            const change = pasteColumnLeft(editorView.state, clip);
            if (change) { editorView.dispatch({ changes: change }); editorView.focus(); }
          } catch {}
        }}><ClipboardPasteIcon /></TBtn>
        <TBtn label="번호열 삽입" className="et-btn et-btn-sm" onMouseDown={e => { e.preventDefault(); act(addNumberColumn); }}><HashIcon /></TBtn>
        <TBtn label="표 삭제" className="et-btn et-btn-sm" onMouseDown={e => { e.preventDefault();
          const change = getDeleteTableChange(editorView.state);
          if (change) { editorView.dispatch({ changes: change }); editorView.focus(); }
        }}><TrashIcon /></TBtn>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════
// Link/Image/Code/List Context (Phase 4)
// ═══════════════════════════════════════

const LinkContextMenu = ({ editorView, link }) => {
  const [editOpen, setEditOpen] = useState(false);
  return (
    <div className="et-context-bar">
      <div className="et-context-label">링크</div>
      <div className="et-group">
        <div className="et-dropdown" style={{ position: 'relative' }}>
          <TBtn label="URL 편집" className="et-btn et-btn-sm" onMouseDown={e => { e.preventDefault(); setEditOpen(!editOpen); }}><EditIcon /></TBtn>
          <LinkPopup isOpen={editOpen} onClose={() => setEditOpen(false)}
            className="et-popup et-link-popup"
            initialText={link.text} initialUrl={link.url}
            onInsert={(text, url) => {
              editorView.dispatch({ changes: { from: link.from, to: link.to, insert: `[${text}](${url})` } });
              editorView.focus();
            }} />
        </div>
        <TBtn label="링크 열기" className="et-btn et-btn-sm" onMouseDown={e => { e.preventDefault(); window.open(link.url, '_blank'); }}><ExternalLinkIcon /></TBtn>
        <TBtn label="연결 해제" className="et-btn et-btn-sm" onMouseDown={e => { e.preventDefault();
          editorView.dispatch({ changes: { from: link.from, to: link.to, insert: link.text } });
          editorView.focus();
        }}><UnlinkIcon /></TBtn>
      </div>
    </div>
  );
};

const ImageContextMenu = ({ editorView, image }) => (
  <div className="et-context-bar">
    <div className="et-context-label">이미지</div>
    <div className="et-group">
      <TBtn label="대체 텍스트 수정" className="et-btn et-btn-sm" onMouseDown={e => { e.preventDefault();
        const newAlt = prompt('대체 텍스트:', image.alt);
        if (newAlt !== null) {
          editorView.dispatch({ changes: { from: image.from, to: image.to, insert: `![${newAlt}](${image.url})` } });
          editorView.focus();
        }
      }}><EditIcon /></TBtn>
      <TBtn label="캡션 추가" className="et-btn et-btn-sm" onMouseDown={e => { e.preventDefault();
        const caption = prompt('캡션 텍스트:', '');
        if (caption !== null && caption.trim()) {
          insertCaption(editorView, caption.trim(), image.from, image.to);
        }
      }}><CaptionIcon /></TBtn>
      <TBtn label="삭제" className="et-btn et-btn-sm" onMouseDown={e => { e.preventDefault();
        editorView.dispatch({ changes: { from: image.from, to: image.to, insert: '' } });
        editorView.focus();
      }}><TrashIcon /></TBtn>
    </div>
  </div>
);

const CodeBlockContextMenu = ({ editorView, lang, from, to }) => {
  const [langOpen, setLangOpen] = useState(false);
  const ref = useRef(null);
  useClickOutside(ref, langOpen, () => setLangOpen(false));
  return (
    <div className="et-context-bar">
      <div className="et-context-label">코드</div>
      <div className="et-group">
        <div className="et-dropdown" ref={ref} style={{ position: 'relative' }}>
          <button className="et-btn et-btn-sm" onMouseDown={e => { e.preventDefault(); setLangOpen(!langOpen); }}>
            <span style={{ fontSize: 11, padding: '0 2px' }}>{lang || 'plain'}</span>
            <ChevronDownIcon />
          </button>
          {langOpen && (
            <div className="et-dropdown-menu" style={{ position: 'absolute', top: '100%', left: 0, zIndex: 200, maxHeight: 200, overflowY: 'auto' }}>
              {CODE_LANGUAGES.map(l => (
                <button key={l} className={`et-dropdown-item ${l === lang ? 'active' : ''}`}
                  onMouseDown={e => { e.preventDefault();
                    const doc = editorView.state.doc;
                    for (let i = 1; i <= doc.lines; i++) {
                      const line = doc.line(i);
                      if (line.from >= from - 5 && line.text.trimStart().startsWith('```')) {
                        const oldLen = line.text.length;
                        const ind = line.text.match(/^(\s*)/)[0];
                        editorView.dispatch({ changes: { from: line.from, to: line.from + oldLen, insert: `${ind}\`\`\`${l}` } });
                        break;
                      }
                    }
                    editorView.focus();
                    setLangOpen(false);
                  }}>{l}</button>
              ))}
            </div>
          )}
        </div>
        <TBtn label="코드 복사" className="et-btn et-btn-sm" onMouseDown={async e => { e.preventDefault();
          const doc = editorView.state.doc;
          let code = '', inside = false;
          for (let i = 1; i <= doc.lines; i++) {
            const line = doc.line(i);
            if (line.from >= from && line.text.trimStart().startsWith('```')) {
              if (!inside) { inside = true; continue; } else break;
            }
            if (inside) code += (code ? '\n' : '') + line.text;
          }
          try { await navigator.clipboard.writeText(code); } catch {}
        }}><CopyIcon /></TBtn>
        <TBtn label="코드 블록 삭제" className="et-btn et-btn-sm" onMouseDown={e => { e.preventDefault();
          let deleteTo = to;
          const doc = editorView.state.doc;
          if (deleteTo < doc.length && doc.sliceString(deleteTo, deleteTo + 1) === '\n') deleteTo++;
          editorView.dispatch({ changes: { from, to: deleteTo, insert: '' } });
          editorView.focus();
        }}><TrashIcon /></TBtn>
      </div>
    </div>
  );
};

const ListContextMenu = ({ editorView, listType }) => (
  <div className="et-context-bar">
    <div className="et-context-label">목록</div>
    <div className="et-group">
      <TBtn label="불릿으로 전환" className={`et-btn et-btn-sm ${listType === 'bullet' ? 'active' : ''}`}
        onMouseDown={e => { e.preventDefault(); toggleBulletList(editorView); editorView.focus(); }}><BulletListIcon /></TBtn>
      <TBtn label="번호로 전환" className={`et-btn et-btn-sm ${listType === 'ordered' ? 'active' : ''}`}
        onMouseDown={e => { e.preventDefault(); toggleOrderedList(editorView); editorView.focus(); }}><OrderedListIcon /></TBtn>
      <TBtn label="체크로 전환" className={`et-btn et-btn-sm ${listType === 'check' ? 'active' : ''}`}
        onMouseDown={e => { e.preventDefault(); toggleCheckList(editorView); editorView.focus(); }}><CheckListIcon /></TBtn>
      <div className="et-sep-sm" />
      <TBtn label="내어쓰기" shortcut={`${shiftKey}+Tab`} className="et-btn et-btn-sm"
        onMouseDown={e => { e.preventDefault(); outdent(editorView); editorView.focus(); }}><OutdentIcon /></TBtn>
      <TBtn label="들여쓰기" shortcut="Tab" className="et-btn et-btn-sm"
        onMouseDown={e => { e.preventDefault(); indent(editorView); editorView.focus(); }}><IndentIcon /></TBtn>
    </div>
  </div>
);

// ═══════════════════════════════════════
// Main Toolbar
// ═══════════════════════════════════════

const EditorToolbar = ({ editorView, onRequestEdit }) => {
  const [formats, setFormats] = useState({});
  const [context, setContext] = useState({});
  const [headingOpen, setHeadingOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [imageOpen, setImageOpen] = useState(false);
  const [tableOpen, setTableOpen] = useState(false);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [insertMoreOpen, setInsertMoreOpen] = useState(false);
  const [specialCharOpen, setSpecialCharOpen] = useState(false);
  const [textColorOpen, setTextColorOpen] = useState(false);
  const [statusLabelOpen, setStatusLabelOpen] = useState(false);
  const [savedSelection, setSavedSelection] = useState(null);
  const intervalRef = useRef(null);
  const linkBtnRef = useRef(null);
  const imageBtnRef = useRef(null);
  const tableBtnRef = useRef(null);
  const emojiBtnRef = useRef(null);
  const specialCharBtnRef = useRef(null);
  const textColorBtnRef = useRef(null);
  const statusLabelBtnRef = useRef(null);

  const closeAll = useCallback(() => {
    setHeadingOpen(false); setMoreOpen(false); setLinkOpen(false);
    setImageOpen(false); setTableOpen(false); setEmojiOpen(false);
    setInsertMoreOpen(false); setSpecialCharOpen(false);
    setTextColorOpen(false); setStatusLabelOpen(false);
  }, []);

  // Escape key closes all popups
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') closeAll(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [closeAll]);

  // Poll active formats + context
  useEffect(() => {
    const poll = () => {
      if (editorView?.state) {
        setFormats(getActiveFormats(editorView.state));
        setContext(getContextInfo(editorView.state));
      }
    };
    poll();
    intervalRef.current = setInterval(poll, 200);
    return () => clearInterval(intervalRef.current);
  }, [editorView]);

  // Save selection before popup open
  const saveSelection = useCallback(() => {
    if (editorView?.state) {
      const { from, to } = editorView.state.selection.main;
      setSavedSelection({ from, to });
    }
  }, [editorView]);

  const run = useCallback((fn, ...args) => {
    if (!editorView) {
      if (onRequestEdit) onRequestEdit();
      return;
    }
    fn(editorView, ...args);
    editorView.focus();
    setTimeout(() => {
      if (editorView?.state) {
        setFormats(getActiveFormats(editorView.state));
        setContext(getContextInfo(editorView.state));
      }
    }, 50);
  }, [editorView, onRequestEdit]);

  // Expose openLinkPopup for Ctrl+K
  const openLinkPopup = useCallback(() => {
    saveSelection();
    closeAll();
    setLinkOpen(true);
  }, [saveSelection, closeAll]);

  // Pass to parent for keyboard shortcut
  useEffect(() => {
    if (editorView) {
      editorView._openLinkPopup = openLinkPopup;
    }
  }, [editorView, openLinkPopup]);

  const disabled = !editorView;
  const currentHeading = formats.heading || 0;
  const headingLabel = formats.blockquote ? '인용문' : currentHeading > 0 ? `제목 ${currentHeading}` : '본문';

  const hasContext = !disabled && (context.inTable || (context.link && !context.image) ||
    context.image || context.inCodeBlock || (context.inList && !context.inTable));

  return (
    <div className={`editor-toolbar-wrapper ${disabled ? 'disabled' : ''}`}>
      {/* ─── Main Toolbar ─── */}
      <div className="editor-toolbar">
        {/* Group 1: Paragraph */}
        <div className="et-group">
          <Dropdown isOpen={headingOpen}
            onToggle={() => { closeAll(); setHeadingOpen(!headingOpen); }}
            onClose={() => setHeadingOpen(false)}
            trigger={<span className="et-heading-trigger">{headingLabel} <ChevronDownIcon /></span>}>
            {HEADING_OPTIONS.map(opt => (
              <button key={opt.value}
                className={`et-dropdown-item et-heading-item ${(opt.value === 'quote' && formats.blockquote) || (opt.value === currentHeading) ? 'active' : ''}`}
                onMouseDown={e => { e.preventDefault();
                  if (opt.value === 'quote') run(toggleBlockquote);
                  else if (opt.value === 'code') run(toggleCodeBlock);
                  else if (opt.value === 0) run(setParagraph);
                  else run(setHeading, opt.value);
                  setHeadingOpen(false);
                }}>
                <span className="et-heading-preview" style={{
                  fontSize: opt.size,
                  fontWeight: opt.weight,
                  fontStyle: opt.italic ? 'italic' : 'normal',
                  fontFamily: opt.mono ? 'monospace' : 'inherit',
                }}>{opt.preview}</span>
                <span className="et-heading-label">{opt.label}</span>
              </button>
            ))}
          </Dropdown>
        </div>

        <div className="et-sep" />

        {/* Group 2: Format */}
        <div className="et-group">
          <TBtn label="볼드" shortcut={`${modKey}+B`} className={`et-btn ${formats.bold ? 'active' : ''}`}
            onMouseDown={e => { e.preventDefault(); run(toggleBold); }}><BoldIcon /></TBtn>
          <TBtn label="이탤릭" shortcut={`${modKey}+I`} className={`et-btn ${formats.italic ? 'active' : ''}`}
            onMouseDown={e => { e.preventDefault(); run(toggleItalic); }}><ItalicIcon /></TBtn>
          <TBtn label="취소선" shortcut={`${modKey}+${shiftKey}+S`} className={`et-btn ${formats.strikethrough ? 'active' : ''}`}
            onMouseDown={e => { e.preventDefault(); run(toggleStrikethrough); }}><StrikethroughIcon /></TBtn>
          <TBtn label="인라인 코드" className={`et-btn ${formats.inlineCode ? 'active' : ''}`}
            onMouseDown={e => { e.preventDefault(); run(toggleInlineCode); }}><CodeIcon /></TBtn>
          <Dropdown isOpen={moreOpen}
            onToggle={() => { closeAll(); setMoreOpen(!moreOpen); }}
            onClose={() => setMoreOpen(false)} trigger={<MoreIcon />}>
            <button className="et-dropdown-item"
              onMouseDown={e => { e.preventDefault(); run(toggleUnderline); setMoreOpen(false); }}>
              <UnderlineIcon /> <span style={{ marginLeft: 6 }}>밑줄</span>
            </button>
            <button className="et-dropdown-item"
              onMouseDown={e => { e.preventDefault(); run(toggleSubscript); setMoreOpen(false); }}>
              <SubscriptIcon /> <span style={{ marginLeft: 6 }}>아랫첨자</span>
            </button>
            <button className="et-dropdown-item"
              onMouseDown={e => { e.preventDefault(); run(toggleSuperscript); setMoreOpen(false); }}>
              <SuperscriptIcon /> <span style={{ marginLeft: 6 }}>윗첨자</span>
            </button>
            <div style={{ borderTop: '1px solid #e5e7eb', margin: '4px 0' }} />
            <button className="et-dropdown-item"
              onMouseDown={e => { e.preventDefault(); run(clearFormatting); setMoreOpen(false); }}>서식 지우기</button>
          </Dropdown>
        </div>

        <div className="et-sep" />

        {/* Group 3: Lists */}
        <div className="et-group">
          <TBtn label="불릿 목록" shortcut={`${modKey}+${shiftKey}+B`} className={`et-btn ${formats.bulletList ? 'active' : ''}`}
            onMouseDown={e => { e.preventDefault(); run(toggleBulletList); }}><BulletListIcon /></TBtn>
          <TBtn label="번호 목록" shortcut={`${modKey}+${shiftKey}+N`} className={`et-btn ${formats.orderedList ? 'active' : ''}`}
            onMouseDown={e => { e.preventDefault(); run(toggleOrderedList); }}><OrderedListIcon /></TBtn>
          <TBtn label="체크 목록" shortcut={`${modKey}+${shiftKey}+L`} className={`et-btn ${formats.checkList ? 'active' : ''}`}
            onMouseDown={e => { e.preventDefault(); run(toggleCheckList); }}><CheckListIcon /></TBtn>
        </div>

        <div className="et-sep" />

        {/* Group 4: Indent */}
        <div className="et-group">
          <TBtn label="내어쓰기" className="et-btn" onMouseDown={e => { e.preventDefault(); run(outdent); }}><OutdentIcon /></TBtn>
          <TBtn label="들여쓰기" shortcut="Tab" className="et-btn" onMouseDown={e => { e.preventDefault(); run(indent); }}><IndentIcon /></TBtn>
        </div>

        <div className="et-sep" />

        {/* Group 5: Insert — popups use Portal to escape overflow:auto */}
        <div className="et-group">
          <span ref={linkBtnRef}>
            <TBtn label="링크" shortcut={`${modKey}+K`} className="et-btn"
              onMouseDown={e => { e.preventDefault(); if (!editorView && onRequestEdit) onRequestEdit(); saveSelection(); closeAll(); setLinkOpen(!linkOpen); }}><LinkIcon /></TBtn>
          </span>
          <PortalPopup anchorRef={linkBtnRef} isOpen={linkOpen} className="et-popup et-link-popup">
            <LinkPopup isOpen={linkOpen} onClose={() => setLinkOpen(false)}
              onInsert={(text, url) => {
                if (editorView) {
                  insertLink(editorView, text, url, savedSelection);
                  editorView.focus();
                }
              }} />
          </PortalPopup>
          <span ref={imageBtnRef}>
            <TBtn label="이미지" className="et-btn"
              onMouseDown={e => { e.preventDefault(); if (!editorView && onRequestEdit) onRequestEdit(); saveSelection(); closeAll(); setImageOpen(!imageOpen); }}><ImageIcon /></TBtn>
          </span>
          <PortalPopup anchorRef={imageBtnRef} isOpen={imageOpen} className="et-popup et-image-popup">
            <ImagePopup isOpen={imageOpen} onClose={() => setImageOpen(false)}
              onInsert={(alt, url) => {
                if (editorView) {
                  insertImage(editorView, alt, url, savedSelection);
                  editorView.focus();
                }
              }} />
          </PortalPopup>
          <span ref={tableBtnRef}>
            <TBtn label="표 삽입" className="et-btn"
              onMouseDown={e => { e.preventDefault(); if (!editorView && onRequestEdit) onRequestEdit(); closeAll(); setTableOpen(!tableOpen); }}><TableIcon /></TBtn>
          </span>
          <PortalPopup anchorRef={tableBtnRef} isOpen={tableOpen} className="et-popup et-table-grid-popup">
            <TableGridPicker isOpen={tableOpen} onClose={() => setTableOpen(false)}
              onInsert={(rows, cols) => run(insertTable, rows, cols)} />
          </PortalPopup>
          <span ref={emojiBtnRef}>
            <TBtn label="이모지" className="et-btn"
              onMouseDown={e => { e.preventDefault(); if (!editorView && onRequestEdit) onRequestEdit(); saveSelection(); closeAll(); setEmojiOpen(!emojiOpen); }}><EmojiIcon /></TBtn>
          </span>
          <PortalPopup anchorRef={emojiBtnRef} isOpen={emojiOpen} className="et-popup et-emoji-popup">
            <EmojiPicker isOpen={emojiOpen} onClose={() => setEmojiOpen(false)}
              onInsert={(emoji) => {
                if (editorView) {
                  insertText(editorView, emoji, savedSelection);
                  editorView.focus();
                }
              }} />
          </PortalPopup>
          <span ref={specialCharBtnRef}>
            <TBtn label="특수문자" className="et-btn"
              onMouseDown={e => { e.preventDefault(); if (!editorView && onRequestEdit) onRequestEdit(); saveSelection(); closeAll(); setSpecialCharOpen(!specialCharOpen); }}><SpecialCharIcon /></TBtn>
          </span>
          <PortalPopup anchorRef={specialCharBtnRef} isOpen={specialCharOpen} className="et-popup et-special-popup">
            <SpecialCharPicker isOpen={specialCharOpen} onClose={() => setSpecialCharOpen(false)}
              onInsert={(ch) => {
                if (editorView) {
                  insertText(editorView, ch, savedSelection);
                  editorView.focus();
                }
              }} />
          </PortalPopup>
          <span ref={textColorBtnRef}>
            <TBtn label="텍스트 색상" className="et-btn"
              onMouseDown={e => { e.preventDefault(); if (!editorView && onRequestEdit) onRequestEdit(); saveSelection(); closeAll(); setTextColorOpen(!textColorOpen); }}><TextColorIcon /></TBtn>
          </span>
          <PortalPopup anchorRef={textColorBtnRef} isOpen={textColorOpen} className="et-popup et-color-popup">
            <TextColorPicker isOpen={textColorOpen} onClose={() => setTextColorOpen(false)}
              onApply={(color) => {
                if (editorView) {
                  if (color === 'remove') {
                    // Remove color: unwrap <span style="color:...">text</span>
                    const { from, to } = savedSelection || editorView.state.selection.main;
                    const sel = editorView.state.doc.sliceString(from, to);
                    const m = sel.match(/^<span style="color:\s*[^"]+">(.+?)<\/span>$/s);
                    if (m) {
                      editorView.dispatch({ changes: { from, to, insert: m[1] } });
                    }
                  } else {
                    insertTextColor(editorView, color);
                  }
                  editorView.focus();
                }
              }} />
          </PortalPopup>
          <Dropdown isOpen={insertMoreOpen}
            onToggle={() => { closeAll(); setInsertMoreOpen(!insertMoreOpen); }}
            onClose={() => setInsertMoreOpen(false)} trigger={<PlusIcon />}>
            <button className="et-dropdown-item" onMouseDown={e => { e.preventDefault(); run(insertHorizontalRule); setInsertMoreOpen(false); }}>가로줄</button>
            <button className="et-dropdown-item" onMouseDown={e => { e.preventDefault(); run(insertDate); setInsertMoreOpen(false); }}>날짜 삽입</button>
            <button className="et-dropdown-item" onMouseDown={e => { e.preventDefault(); run(insertTOC); setInsertMoreOpen(false); }}>목차 (TOC)</button>
            <div style={{ borderTop: '1px solid #e5e7eb', margin: '4px 0' }} />
            <button className="et-dropdown-item" onMouseDown={e => { e.preventDefault(); run(insertCallout, 'INFO'); setInsertMoreOpen(false); }}>정보 패널</button>
            <button className="et-dropdown-item" onMouseDown={e => { e.preventDefault(); run(insertCallout, 'WARNING'); setInsertMoreOpen(false); }}>경고 패널</button>
            <button className="et-dropdown-item" onMouseDown={e => { e.preventDefault(); run(insertCallout, 'TIP'); setInsertMoreOpen(false); }}>팁 패널</button>
            <button className="et-dropdown-item" onMouseDown={e => { e.preventDefault(); run(insertCallout, 'IMPORTANT'); setInsertMoreOpen(false); }}>중요 패널</button>
            <button className="et-dropdown-item" onMouseDown={e => { e.preventDefault(); run(insertCallout, 'CAUTION'); setInsertMoreOpen(false); }}>주의 패널</button>
            <div style={{ borderTop: '1px solid #e5e7eb', margin: '4px 0' }} />
            <span ref={statusLabelBtnRef} style={{ display: 'block' }}>
              <button className="et-dropdown-item" onMouseDown={e => {
                e.preventDefault();
                setInsertMoreOpen(false);
                saveSelection();
                setTimeout(() => setStatusLabelOpen(true), 50);
              }}>상태 라벨</button>
            </span>
          </Dropdown>
          <PortalPopup anchorRef={statusLabelBtnRef} isOpen={statusLabelOpen} className="et-popup et-status-popup">
            <StatusLabelPicker isOpen={statusLabelOpen} onClose={() => setStatusLabelOpen(false)}
              onInsert={(status) => {
                if (editorView) {
                  insertStatusLabel(editorView, status);
                  editorView.focus();
                }
              }} />
          </PortalPopup>
        </div>

        <div className="et-sep" />

        {/* Group 6: Undo/Redo */}
        <div className="et-group">
          <TBtn label="실행 취소" shortcut={`${modKey}+Z`} className="et-btn" onMouseDown={e => { e.preventDefault(); run(doUndo); }}><UndoIcon /></TBtn>
          <TBtn label="다시 하기" shortcut={`${modKey}+Y`} className="et-btn" onMouseDown={e => { e.preventDefault(); run(doRedo); }}><RedoIcon /></TBtn>
        </div>
      </div>

      {/* ─── Context Sub-Menus (overlay, no layout shift) ─── */}
      {hasContext && (
        <div className="et-context-overlay">
          {context.inTable && <TableContextMenu editorView={editorView} />}
          {context.link && !context.image && <LinkContextMenu editorView={editorView} link={context.link} />}
          {context.image && <ImageContextMenu editorView={editorView} image={context.image} />}
          {context.inCodeBlock && <CodeBlockContextMenu editorView={editorView} lang={context.codeBlockLang} from={context.codeBlockFrom} to={context.codeBlockTo} />}
          {context.inList && !context.inTable && <ListContextMenu editorView={editorView} listType={context.listType} />}
        </div>
      )}
    </div>
  );
};

export default EditorToolbar;
